<template>
  <el-container class="project-container">
    <!-- 左侧窄图标导航 (60px) -->
    <el-aside width="60px" class="icon-sidebar">
      <side-menu></side-menu>
    </el-aside>
    
    <el-container class="main-container">
      <!-- 顶部导航栏：面包屑/项目选择器 + 子菜单 + 用户信息在同一行 -->
      <el-header class="header" height="50px">
        <div class="header-bar">
          <!-- 左侧：面包屑 + 项目选择器 + 子菜单 -->
          <div class="nav-area">
            <!-- 面包屑：项目列表 -->
            <router-link to="/project" class="breadcrumb-link">
              <i class="el-icon-folder"></i>
              项目列表
            </router-link>
            
            <!-- 项目选择器（仅在项目详情页显示） -->
            <template v-if="showProjectSelector">
              <span class="breadcrumb-separator">/</span>
              <el-dropdown @command="onProjectChange" trigger="click">
                <span class="project-selector">
                  项目: {{ currentProjectName }}
                  <i class="el-icon-arrow-down"></i>
                </span>
                <el-dropdown-menu slot="dropdown" class="project-dropdown-menu">
                  <el-dropdown-item 
                    v-for="proj in projectList" 
                    :key="proj.id" 
                    :command="proj.id"
                    :class="{ 'is-active': proj.id === currentProjectId }"
                  >
                    {{ proj.name }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              
              <!-- 项目子菜单 Tab -->
              <div class="sub-menu-tabs">
                <router-link
                  v-for="tab in projectTabs"
                  :key="tab.name"
                  :to="getTabPath(tab)"
                  class="sub-menu-tab"
                  :class="{ 'is-active': isTabActive(tab) }"
                >
                  {{ tab.label }}
                </router-link>
              </div>
            </template>
          </div>
          
          <!-- 右侧：管理后台 + 用户信息 -->
          <div class="user-area">
            <el-button 
              v-if="isAdmin" 
              type="primary" 
              size="small" 
              @click="gotoAdmin"
              class="admin-btn"
            >
              管理后台
            </el-button>
            <el-dropdown @command="handleUserCommand">
              <span class="user-dropdown">
                <i class="el-icon-user-solid"></i>
                <span class="user-name">{{ userName }}</span>
                <i class="el-icon-arrow-down"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="change-password">修改密码</el-dropdown-item>
                <el-dropdown-item command="sign-out" divided>退出</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </el-header>
      
      <!-- 主内容区 -->
      <el-main class="layout-main">
        <el-scrollbar id="mainScroll" style="height: 100%;">
          <div class="main_bg" id="container">
            <div class="content-wrap">
              <keep-alive :max="4" :include="$store.state.cached">
                <router-view />
              </keep-alive>
            </div>
          </div>
        </el-scrollbar>
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import SideMenu from './SideMenu'
import { signOut } from 'index/api'
import { getProjectList } from 'index/api/project/manager'

export default {
  name: 'ProjectLayout',

  components: {
    SideMenu
  },

  data() {
    return {
      projectList: [],
      // 默认子菜单配置（当权限表无二级菜单时使用）
      defaultTabs: [
        { name: 'overview', label: '概览', path: 'overview' },
        { name: 'forms', label: '我的问卷', path: 'forms' },
        { name: 'waves', label: '波次', path: 'waves' },
        { name: 'data', label: '数据管理', path: 'data' },
        { name: 'segment-config', label: '环节配置', path: 'segment-config' },
        { name: 'store-report', label: '单店报告', path: 'store-report' },
        { name: 'file-distribution', label: '报告下发', path: 'file-distribution' },
        { name: 'members', label: '成员管理', path: 'members' },
        { name: 'settings', label: '设置', path: 'settings' },
        { name: 'appeal', label: '申诉处理', path: 'appeal' }
      ]
    }
  },

  computed: {
    userInfo() {
      return this.$store.getters.userInfo || {}
    },
    userName() {
      return this.userInfo.name || this.userInfo.username || 'admin'
    },
    // 是否为管理员（仅admin用户名）
    isAdmin() {
      const u = this.userInfo || {}
      return u.username === 'admin' || u.name === 'admin'
    },
    // 是否显示项目选择器和子菜单（仅在项目详情页显示）
    showProjectSelector() {
      const path = this.$route.path
      // 匹配 /project/:projectId 及其子路由
      return /^\/project\/\d+/.test(path)
    },
    // 当前项目ID
    currentProjectId() {
      const id = this.$route.params.projectId
      return id ? parseInt(id) : null
    },
    // 当前项目名称
    currentProjectName() {
      if (!this.currentProjectId) return '选择项目'
      const proj = this.projectList.find(p => p.id === this.currentProjectId)
      return proj ? proj.name : '加载中...'
    },
    // 项目详情子菜单（从 surveyRoutes 二级菜单读取）
    projectTabs() {
      const surveyRoutes = this.$store.state.surveyRoutes || []
      // 找到 /project 一级菜单
      const projectMenu = surveyRoutes.find(r => r.uri === '/project' || r.uri === '/')
      // 如果有子菜单则使用
      if (projectMenu && projectMenu.children && projectMenu.children.length > 0) {
        return projectMenu.children
          .filter(item => {
            // 只保留项目详情子菜单（URI包含 :projectId 的）
            // 过滤掉项目列表页（/project）
            return item.is_menu === '1' && 
                   item.uri && 
                   item.uri.includes(':projectId')
          })
          .map(item => {
            // 从 uri 提取 path (如 /project/:projectId/forms -> forms)
            const pathMatch = item.uri && item.uri.match(/\/project\/:projectId\/(.+)/)
            const path = pathMatch ? pathMatch[1] : item.uri
            return {
              name: path,
              label: item.name,
              path: path,
              uri: item.uri
            }
          })
      }
      // 使用默认子菜单
      return this.defaultTabs
    }
  },

  watch: {
    showProjectSelector: {
      immediate: true,
      handler(val) {
        if (val && this.projectList.length === 0) {
          this.loadProjects()
        }
      }
    }
  },

  methods: {
    async loadProjects() {
      try {
        const res = await getProjectList({ page: 1, pageSize: 100 })
        // 响应结构: res.data = {success, errcode, message, data: {records, total, size}}
        const records = res.data?.data?.records || res.data?.records || []
        this.projectList = records
      } catch (e) {
        // axios拦截器拒绝时数据在 e.data 中
        const records = e?.data?.data?.records || e?.data?.records
        if (records && records.length > 0) {
          this.projectList = records
        } else {
          console.error('加载项目列表失败', e)
        }
      }
    },
    
    getTabPath(tab) {
      return `/project/${this.currentProjectId}/${tab.path}`
    },
    
    isTabActive(tab) {
      const path = this.$route.path
      return path.includes(`/${tab.path}`) || 
             (tab.name === 'overview' && path.match(/^\/project\/\d+$/))
    },
    
    onProjectChange(projectId) {
      if (projectId !== this.currentProjectId) {
        // 切换项目，保持当前 tab
        const currentTab = this.$route.path.split('/').pop()
        const validTabs = this.projectTabs.map(t => t.path)
        const targetTab = validTabs.includes(currentTab) ? currentTab : 'overview'
        this.$router.push(`/project/${projectId}/${targetTab}`)
      }
    },
    
    handleUserCommand(command) {
      switch (command) {
        case 'change-password':
          this.$router.push('/change-password')
          break
        case 'sign-out':
          signOut().then(() => {
            localStorage.removeItem('ACCESS_TOKEN')
            window.location.href = '/'
          }).finally(() => {
            localStorage.removeItem('ACCESS_TOKEN')
            window.location.href = '/'
          })
          break
      }
    },
    
    // 跳转到管理后台
    gotoAdmin() {
      window.location.href = '/admin'
    }
  }
}
</script>

<style lang="scss" scoped>
.project-container {
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
}

.main-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 左侧图标导航栏 */
.icon-sidebar {
  background: #fff !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 12px;
  z-index: 100;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.06);
}

/* 顶部导航栏 */
.el-header {
  margin: 0;
  padding: 0;
  background-color: #fff;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  z-index: 99;
}

.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 50px;
  padding: 0 20px;
}

/* 导航区域 */
.nav-area {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  overflow-x: auto;
}

.breadcrumb-link {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
  white-space: nowrap;
  
  &:hover {
    color: #409EFF;
  }
  
  i {
    font-size: 16px;
  }
}

.breadcrumb-separator {
  color: #c0c4cc;
  margin: 0 4px;
}

.project-selector {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #409EFF;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    color: #66b1ff;
  }
}

.project-dropdown-menu {
  max-height: 300px;
  overflow-y: auto;
  
  .is-active {
    color: #409EFF;
    font-weight: 500;
  }
}

/* 项目子菜单 Tab */
.sub-menu-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 16px;
  padding-left: 16px;
  border-left: 1px solid #e4e7ed;
}

.sub-menu-tab {
  padding: 6px 14px;
  font-size: 14px;
  color: #666;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    color: #409EFF;
    background: #f5f7fa;
  }
  
  &.is-active {
    color: #409EFF;
    font-weight: 500;
    background: #ecf5ff;
  }
}

/* 用户区域 */
.user-area {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.user-dropdown {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  
  &:hover {
    color: #409EFF;
  }
}

.user-name {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 主内容区 */
.layout-main {
  flex: 1;
  background-color: #f5f7fa;
  padding: 16px;
  overflow: hidden;
  
  .main_bg {
    height: 100%;
    overflow: hidden;
  }
  
  .content-wrap {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    min-height: calc(100vh - 130px);
  }
}

/deep/ .el-scrollbar__wrap {
  overflow-x: hidden;
}
</style>

<!-- 非 scoped 样式确保覆盖全局 -->
<style lang="scss">
.project-container .icon-sidebar.el-aside {
  background: #fff !important;
  background-color: #fff !important;
}
</style>
